import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import request from "../../../../utils/Request.utils";
import { isDetailNotFound, isValidDetailId } from "../../../../utils/DetailPage.utils";
import { checkIdPattern, checkPasswordPatternCascade, validateEmail } from "../../../../utils/Common.utils";
import ErrorPage from "../../error/ErrorPage";

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 16;

const initialForm = {
  admin_id: "",
  admin_name: "",
  admin_email: "",
  admin_phone: "",
  admin_pwd: "",
  admin_pwd_confirm: "",
};

const SubAdminWrite = () => {
  const navigate = useNavigate();
  const { admin_seq } = useParams();
  const isEdit = Boolean(admin_seq);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadSubAdmin = useCallback((isActive = () => true) => {
    if (!isEdit) return;
    if (!isValidDetailId(admin_seq)) {
      setNotFound(true);
      return;
    }

    setIsLoading(true);
    request.get(`/admin/sub-admin/${admin_seq}`, {}, (response) => {
      if (!isActive()) return;
      if (isDetailNotFound(response, response?.data)) {
        setNotFound(true);
        return;
      }
      setIsLoading(false);
      if (response?.messageCode !== 200) {
        toast.error(response?.messageString || "부관리자 정보를 불러올 수 없습니다.");
        return;
      }

      setForm({
        admin_id: response.data.admin_id || "",
        admin_name: response.data.admin_name || "",
        admin_email: response.data.admin_email || "",
        admin_phone: response.data.admin_phone || "",
        admin_pwd: "",
        admin_pwd_confirm: "",
      });
    }, (error) => {
      if (!isActive()) return;
      if (isDetailNotFound(error)) {
        setNotFound(true);
        return;
      }
      setIsLoading(false);
      toast.error("부관리자 정보를 불러오는 중 오류가 발생했습니다.");
    });
  }, [admin_seq, isEdit]);

  useEffect(() => {
    setNotFound(false);
    setIsLoading(isEdit);
    let active = true;
    loadSubAdmin(() => active);
    return () => { active = false; };
  }, [loadSubAdmin, isEdit]);

  const validateForm = () => {
    const adminId = form.admin_id.trim();
    const adminName = form.admin_name.trim();
    const adminEmail = form.admin_email.trim();
    const password = form.admin_pwd.trim();
    const passwordConfirm = form.admin_pwd_confirm.trim();

    if (!adminId) {
      toast.error("아이디를 입력해 주세요.");
      return false;
    }

    if (!isEdit && (!checkIdPattern(adminId, 4) || adminId.length > 30)) {
      toast.error("아이디는 영문/숫자 4~30자리로 입력해 주세요.");
      return false;
    }

    if (!adminName) {
      toast.error("이름을 입력해 주세요.");
      return false;
    }

    if (adminEmail && !validateEmail(adminEmail)) {
      toast.error("이메일 형식이 올바르지 않습니다.");
      return false;
    }

    if (!isEdit && !password) {
      toast.error("비밀번호를 입력해 주세요.");
      return false;
    }

    if ((password || passwordConfirm) && password !== passwordConfirm) {
      toast.error("비밀번호가 일치하지 않습니다.");
      return false;
    }

    if (password && (
      password.length > PASSWORD_MAX_LENGTH ||
      !checkPasswordPatternCascade(password, PASSWORD_MIN_LENGTH)
    )) {
      toast.error("비밀번호는 영문, 숫자, 특수문자 중 2가지 이상 포함 8~16자리로 입력해 주세요.");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const params = {
      admin_id: form.admin_id.trim(),
      admin_name: form.admin_name.trim(),
      admin_email: form.admin_email.trim(),
      admin_phone: form.admin_phone.trim(),
    };

    if (form.admin_pwd.trim()) {
      params.admin_pwd = form.admin_pwd.trim();
    }

    setIsSubmitting(true);

    const successHandler = (response) => {
      setIsSubmitting(false);
      if (response.messageCode !== 200) {
        toast.error(response.messageString || `부관리자 ${isEdit ? "수정" : "등록"} 중 오류가 발생했습니다.`);
        return;
      }

      toast.success(`부관리자가 ${isEdit ? "수정" : "등록"}되었습니다.`);
      navigate("/Setting/Manager/SubAdmin");
    };

    const failHandler = () => {
      setIsSubmitting(false);
      toast.error(`부관리자 ${isEdit ? "수정" : "등록"} 중 오류가 발생했습니다.`);
    };

    if (isEdit) {
      request.put(`/admin/sub-admin/${admin_seq}`, params, successHandler, failHandler);
    } else {
      request.post("/admin/sub-admin", params, successHandler, failHandler);
    }
  };

  if (notFound) return <ErrorPage />;

  return (
    <main>
      <h2 className="adm_com_pg_tit">부관리자 계정 관리</h2>
      <table className="adm_com_board_list type_row">
        <colgroup>
          <col width="14%" /><col />
        </colgroup>
        <tbody>
          <tr>
            <th>아이디</th>
            <td>
              <input
                type="text"
                className="input"
                value={form.admin_id}
                maxLength={30}
                disabled={isEdit || isLoading}
                onChange={(e) => handleChange("admin_id", e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>이름</th>
            <td>
              <input
                type="text"
                className="input"
                value={form.admin_name}
                maxLength={30}
                disabled={isLoading}
                onChange={(e) => handleChange("admin_name", e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>이메일</th>
            <td>
              <input
                type="email"
                className="input"
                value={form.admin_email}
                maxLength={100}
                disabled={isLoading}
                onChange={(e) => handleChange("admin_email", e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>휴대폰</th>
            <td>
              <input
                type="text"
                className="input"
                value={form.admin_phone}
                maxLength={16}
                disabled={isLoading}
                onChange={(e) => handleChange("admin_phone", e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>{isEdit ? "새 비밀번호" : "비밀번호"}</th>
            <td>
              <input
                type="password"
                className="input"
                value={form.admin_pwd}
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                disabled={isLoading}
                onChange={(e) => handleChange("admin_pwd", e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>비밀번호 확인</th>
            <td>
              <input
                type="password"
                className="input"
                value={form.admin_pwd_confirm}
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                disabled={isLoading}
                onChange={(e) => handleChange("admin_pwd_confirm", e.target.value)}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="adm_com_btn_wrap center">
        <button className="adm_com_btn blue" onClick={handleSubmit} disabled={isLoading || isSubmitting}>
          {isEdit ? "수정" : "등록"}
        </button>
        <button className="adm_com_btn gray" onClick={() => navigate("/Setting/Manager/SubAdmin")} disabled={isSubmitting}>
          취소
        </button>
      </div>
      <ToastContainer />
    </main>
  );
};

export default SubAdminWrite;
